import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hammer, CaretDown, CaretRight } from '@phosphor-icons/react';
import ElementDetailDrawer from './ElementDetailDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  reserve: '#C75B39',
  tache: '#1E3A5F',
  incident: '#C00000',
  non_conformite: '#8B3A3A',
  observation: '#6B6B6B',
};

function fmtFr(d) {
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/**
 * REFONTE Session 5.4 — Swimlane "Éléments terrain" affichée sous le Gantt classique.
 * Lit db.elements (etat=actif, echeance non null) et trace une barre par élément
 * sur l'axe temporel echeance - duree_jours → echeance.
 *
 * Affichage compact : 1 ligne par élément, hauteur 20px par barre, 5 jours visibles
 * de marge avant et 5 après la fenêtre globale du Gantt.
 */
export default function ElementsTerrainSwimlane({ chantierId, dateMin, dateMax }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [activeElement, setActiveElement] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/chantiers/${chantierId}/elements?etat=actif&limit=300`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const arr = (Array.isArray(r.data) ? r.data : r.data?.items || [])
          .filter((e) => e.echeance && (e.duree_jours || 1) > 0);
        // tri par échéance
        arr.sort((a, b) => (a.echeance || '').localeCompare(b.echeance || ''));
        setItems(arr);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chantierId]);

  // Fenêtre temporelle dynamique
  const dMin = dateMin ? new Date(dateMin) : (items.length > 0
    ? new Date(items[0].echeance) : new Date());
  const dMax = dateMax ? new Date(dateMax) : (items.length > 0
    ? new Date(items[items.length - 1].echeance) : new Date());

  // Marge ±5 jours pour respiration
  const start = new Date(dMin);
  start.setDate(start.getDate() - 5);
  const end = new Date(dMax);
  end.setDate(end.getDate() + 5);
  const totalDays = Math.max(1, Math.round((end - start) / 86400000));

  if (loading) {
    return (
      <div className="bg-white border border-[#E0E0DB] mt-4 p-4 text-xs text-[#9A9A9A]" data-testid="elements-swimlane-loading">
        Chargement des observations et actions…
      </div>
    );
  }

  if (items.length === 0) {
    return null; // pas de swimlane si rien à afficher
  }

  return (
    <div className="bg-white border border-[#E0E0DB] mt-4" data-testid="elements-terrain-swimlane">
      {/* Header */}
      <button
        className="w-full px-4 py-2.5 bg-[#FAFAF8] flex items-center gap-3 hover:bg-[#F5F5F3] transition-colors"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="elements-swimlane-toggle"
      >
        {collapsed ? <CaretRight size={14} /> : <CaretDown size={14} />}
        <Hammer size={16} style={{ color: '#C4773B' }} />
        <h4 className="text-sm font-bold text-[#2C2C2C]">Observations et actions</h4>
        <span className="text-xs text-[#9A9A9A]">({items.length} avec échéance)</span>
      </button>

      {!collapsed && (
        <div className="p-3 overflow-x-auto" data-testid="elements-swimlane-content">
          {/* Header dates (4 graduations) */}
          <div className="relative h-5 border-b border-[#E0E0DB] mb-1" style={{ minWidth: 600 }}>
            {[0, 0.25, 0.5, 0.75, 1].map((p) => {
              const d = new Date(start);
              d.setDate(d.getDate() + Math.round(totalDays * p));
              return (
                <div
                  key={p}
                  className="absolute top-0 text-[10px] text-[#9A9A9A]"
                  style={{ left: `${p * 100}%`, transform: p === 1 ? 'translateX(-100%)' : (p === 0 ? 'none' : 'translateX(-50%)') }}
                >
                  {fmtFr(d)}
                </div>
              );
            })}
          </div>

          {/* Bars */}
          <div className="space-y-1.5" style={{ minWidth: 600 }}>
            {items.map((e) => {
              const eid = e._id || e.id;
              const endD = new Date(e.echeance);
              const dur = Math.max(1, parseInt(e.duree_jours, 10) || 1);
              const startD = new Date(endD);
              startD.setDate(startD.getDate() - dur);
              // Clamp sur la fenêtre
              const clampStart = Math.max(0, (startD - start) / 86400000);
              const clampEnd = Math.min(totalDays, (endD - start) / 86400000);
              const left = (clampStart / totalDays) * 100;
              const width = Math.max(1, ((clampEnd - clampStart) / totalDays) * 100);
              const color = e.priorite === 'urgent' ? '#C00000' : (TYPE_COLORS[e.type] || '#C4773B');
              return (
                <div key={eid} className="relative h-5" data-testid={`elements-swimlane-bar-${eid}`}>
                  <button
                    className="absolute top-0 h-5 hover:opacity-90 hover:shadow-sm transition-all flex items-center px-2 text-[10px] text-white font-semibold truncate"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      background: color,
                      minWidth: 20,
                    }}
                    onClick={() => {
                      // CHA-09 — tâche legacy (db.taches) : le panneau détail pilote des
                      // endpoints /elements/{id}/… qui ne la connaissent pas (404).
                      if (e.source_table === 'taches') {
                        toast.info('Ancienne action : clôturez-la depuis l\'onglet Actions (« Marquer levée » / « Sans suite »).');
                        return;
                      }
                      setActiveElement(e);
                      setDrawerOpen(true);
                    }}
                    title={`${e.titre || ''} — du ${fmtFr(startD)} au ${fmtFr(endD)} · ${e.entreprise || '?'}`}
                  >
                    {e.titre || e.texte_brut || ''}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-[10px] text-[#9A9A9A] mt-3 italic">
            Cliquez sur une barre pour ouvrir l&apos;élément. Couleur rouge = priorité urgente.
          </p>
        </div>
      )}

      <ElementDetailDrawer
        element={activeElement}
        chantierId={chantierId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdate={() => {
          // Recharge les éléments
          axios.get(`${API_URL}/api/chantiers/${chantierId}/elements?etat=actif&limit=300`, { withCredentials: true })
            .then((r) => {
              const arr = (Array.isArray(r.data) ? r.data : r.data?.items || [])
                .filter((e) => e.echeance && (e.duree_jours || 1) > 0);
              arr.sort((a, b) => (a.echeance || '').localeCompare(b.echeance || ''));
              setItems(arr);
            })
            .catch(() => { /* noop */ });
        }}
        onRemove={(id) => {
          setItems((prev) => prev.filter((x) => (x._id || x.id) !== id));
        }}
      />
    </div>
  );
}
